import { createSlice } from '@reduxjs/toolkit'
import {
  clearAlertStore,
  clearAuthorizedUser,
  userAuthLogin,
  userAuthLogout,
  userAuthResetCheckToken,
  userAuthResetPassword,
  userAuthSamlExchange,
  userAuthSamlLoginCheck,
  userAuthResolveSsoByDomain,
  userAuthValidateToken,
  userAuthChangePassword,
  userCustomerAdminAuthChangePassword,
  userAuthResetAlertHandler,
  sendForgetPasswordEmail,
  validateResetPasswordToken,
  userResetPassword,
  userAuthTokenDecode,
  userAuthGetPolicies,
  userAuthAcceptPolicies,
} from './userAuthThunk'
import { enrollBiometricLogin, userAuthLoginWithVault } from './biometricThunk'

const initialState = {
  alert: {},
  authorizedUser: {},
  loading: false,
  policies: {},
  promptBiometricEnrollment: false,
}

const userAuthSlice = createSlice({
  name: 'userAuth',
  initialState,
  reducers: {
    clearBiometricEnrollmentPrompt: (state) => {
      state.promptBiometricEnrollment = false
    },
  },
  extraReducers: {
    [userAuthResetAlertHandler.fulfilled]: (state) => {
      state.alert = {}
    },
    [userAuthLogin.pending]: (state) => {
      state.loading = true
      state.alert = {}
    },
    [userAuthLogin.rejected]: (state, { payload }) => {
      state.loading = false
      state.alert = payload
    },
    [userAuthLogin.fulfilled]: (state) => {
      state.alert = {}
      state.loading = false
      // Picked up by BiometricEnrollmentGate at the app root, which offers
      // enrollment after the login route has already redirected away.
      state.promptBiometricEnrollment = true
    },
    [userAuthTokenDecode.fulfilled]: (state, { payload }) => {
      state.alert = {}
      state.loading = false
      state.authorizedUser = {
        ...payload,
      }
    },
    [userAuthLogout.pending]: (state) => {
      state.loading = true
      state.alert = {}
    },
    [userAuthLogout.fulfilled]: (state) => {
      state.alert = {}
      state.loading = false
    },
    [clearAuthorizedUser.fulfilled]: (state) => {
      state.alert = {}
      state.loading = false
      state.authorizedUser = {}
    },
    [userAuthResetPassword.pending]: (state) => {
      state.loading = true
      state.alert = {}
    },
    [userAuthResetPassword.rejected]: (state, { payload }) => {
      state.loading = false
      state.alert = payload
    },
    [userAuthResetPassword.fulfilled]: (state, { payload }) => {
      state.loading = false
      state.alert = payload
    },
    [sendForgetPasswordEmail.pending]: (state) => {
      state.loading = true
      state.alert = {}
    },
    [sendForgetPasswordEmail.rejected]: (state, { payload }) => {
      state.loading = false
      state.alert = payload
    },
    [sendForgetPasswordEmail.fulfilled]: (state, { payload }) => {
      state.loading = false
      state.alert = payload
    },
    [validateResetPasswordToken.pending]: (state) => {
      state.loading = true
      state.alert = {}
    },
    [validateResetPasswordToken.rejected]: (state, { payload }) => {
      state.loading = false
      state.alert = payload
    },
    [validateResetPasswordToken.fulfilled]: (state) => {
      state.loading = false
      state.alert = {}
    },
    [userResetPassword.pending]: (state) => {
      state.loading = true
      state.alert = {}
    },
    [userResetPassword.rejected]: (state, { payload }) => {
      state.loading = false
      state.alert = payload
    },
    [userResetPassword.fulfilled]: (state, { payload }) => {
      state.loading = false
      state.alert = payload
    },
    [userAuthResetCheckToken.pending]: (state) => {
      state.loading = true
      state.alert = {}
    },
    [userAuthResetCheckToken.rejected]: (state, { payload }) => {
      state.loading = false
      state.alert = payload
    },
    [userAuthResetCheckToken.fulfilled]: (state, { payload }) => {
      state.loading = false
      state.alert = payload
    },
    [userAuthSamlExchange.pending]: (state) => {
      state.loading = true
      state.alert = {}
    },
    [userAuthSamlExchange.rejected]: (state, { payload }) => {
      state.loading = false
      state.alert = payload
    },
    [userAuthSamlExchange.fulfilled]: (state) => {
      state.loading = false
      state.alert = {}
    },
    [userAuthSamlLoginCheck.pending]: (state) => {
      state.loading = true
      state.alert = {}
    },
    [userAuthSamlLoginCheck.rejected]: (state, { payload }) => {
      state.loading = false
      state.alert = payload
    },
    [userAuthSamlLoginCheck.fulfilled]: (state) => {
      state.loading = false
      state.alert = {}
    },
    [userAuthResolveSsoByDomain.pending]: (state) => {
      state.loading = true
      state.alert = {}
    },
    [userAuthResolveSsoByDomain.rejected]: (state, { payload }) => {
      state.loading = false
      state.alert = payload || {}
    },
    [userAuthResolveSsoByDomain.fulfilled]: (state) => {
      state.loading = false
      state.alert = {}
    },
    [userAuthValidateToken.pending]: (state) => {
      state.loading = true
      state.alert = {}
    },
    [userAuthValidateToken.rejected]: (state, { payload }) => {
      state.loading = false
      state.alert = payload
    },
    [userAuthValidateToken.fulfilled]: (state) => {
      state.loading = false
      state.alert = {}
    },
    [clearAlertStore.fulfilled]: (state) => {
      state.loading = false
      state.alert = {}
    },
    [userAuthChangePassword.pending]: (state) => {
      state.loading = true
      state.alert = {}
    },
    [userAuthChangePassword.rejected]: (state, { payload }) => {
      state.loading = false
      state.alert = payload
    },
    [userAuthChangePassword.fulfilled]: (state) => {
      state.loading = false
      state.alert = {}
    },
    [userCustomerAdminAuthChangePassword.pending]: (state) => {
      state.loading = true
      state.alert = {}
    },
    [userCustomerAdminAuthChangePassword.rejected]: (state, { payload }) => {
      state.loading = false
      state.alert = payload
    },
    [userCustomerAdminAuthChangePassword.fulfilled]: (state) => {
      state.loading = false
      state.alert = {}
    },
    [userAuthGetPolicies.pending]: (state) => {
      state.loading = true
      state.alert = {}
    },
    [userAuthGetPolicies.rejected]: (state, { payload }) => {
      state.loading = false
      state.alert = payload
    },
    [userAuthGetPolicies.fulfilled]: (state, { payload }) => {
      state.loading = false
      state.alert = {}
      state.policies = payload
    },
    [userAuthAcceptPolicies.pending]: (state) => {
      state.loading = true
      state.alert = {}
    },
    [userAuthAcceptPolicies.rejected]: (state, { payload }) => {
      state.loading = false
      state.alert = payload
    },
    [userAuthAcceptPolicies.fulfilled]: (state) => {
      state.loading = false
      state.alert = {}
    },
    [enrollBiometricLogin.rejected]: (state, { payload }) => {
      // Dismissing the OS prompt is a deliberate choice, not an error to report.
      state.alert = payload?.cancelled ? {} : payload
    },
    [userAuthLoginWithVault.pending]: (state) => {
      state.loading = true
      state.alert = {}
    },
    [userAuthLoginWithVault.rejected]: (state, { payload }) => {
      state.loading = false
      // A cancelled OS prompt is not an error worth showing - the user just
      // falls back to the password form that is already on screen.
      state.alert = payload?.cancelled ? {} : payload
    },
    [userAuthLoginWithVault.fulfilled]: (state) => {
      state.loading = false
      state.alert = {}
    },
  },
})

export const selectAuthUser = ({ userAuth }) => userAuth

export const { clearBiometricEnrollmentPrompt } = userAuthSlice.actions

export default userAuthSlice.reducer
